import { yupResolver } from '@hookform/resolvers/yup'
import { find, findIndex, map } from 'lodash-es'
import { useContext, useEffect, useRef, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import { Days, Languages } from '../../../../common/models'
import FormGroup from '../../../../core/components/FormGroup'
import {
  formatDateTime,
  formatDateTimeInOffset,
  parseDateTimeInOffset,
  parseFormDateTime,
} from '../../../../core/constants/times'
import { EditionChange } from '../../../../core/context/EditionChangeContext'
import {
  deleteFestivalDays,
  getFestivalDays,
  updateFestivalDays,
} from '../../../../core/services/dates-settings'
import { getEditions } from '../../../../core/services/festival-settings'
import { useAppSelector } from '../../../../store'
import { DeleteConfirmationModal } from '../../DeleteConfirmationModal'
import { MessageBox } from '../../messages/MessageBox'
import { DateTimePicker } from '@/components/ui/date-time-picker'
import { Card, Button, Checkbox, Input } from '@/components/ui'
import { Icon } from '@/common/components/Icon'

export interface IDatesFormValues {
  days:
    | {
        dId?: string
        startDate: string
        endDate: string
        translations: {
          locale: string
          name: string
        }[]
        festivalEdition: {
          id: string
          name: string
          startDate: number
          endDate: number
          active: boolean
          backstageEventCode?: string | null
        }
      }[]
}

export interface FestivalDatesProps {
  days: Days[]
  setDays: (d: Days[]) => void
  allLanguages: Languages[]
  hasEditPermission: boolean
}

export const FestivalDates: React.FC<FestivalDatesProps> = ({
  days,
  allLanguages,
  setDays,
  hasEditPermission,
}) => {
  const { festivalName } = useParams()
  const { festivals: storeFestivals } = useAppSelector((s) => s.app)
  const editionId = storeFestivals
    .find((f) => f.platformName === festivalName)
    ?.editions.find((e) => e.active)?.id
  const [showDates, setShowDates] = useState<boolean>(true)
  const [day, setDay] = useState<Days[]>(days)
  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)
  const [deleteId, setDeleteId] = useState<string>()
  const [deleteIndex, setDeleteIndex] = useState<number>()
  const formRef = useRef<HTMLFormElement>(null)
  const { editionChanged } = useContext(EditionChange)
  const [timezone, setTimezone] = useState<string | undefined>()

  useEffect(() => {
    if (!editionId) return
    getEditions()
      .then((resp) => {
        setTimezone(resp.data.find((e) => e.id === editionId)?.localTime)
      })
      .catch(console.error)
  }, [editionId, editionChanged])

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        setDay(days)
        if (editionId) {
          const temp = map(days, d => {
            const transTemp = map(allLanguages, (lang) => {
              return {
                locale: lang.locale,
                name: '',
              }
            })
            map(d.translations, trans => {
              const index = findIndex(allLanguages, l => l.locale === trans.locale)
              if (index !== -1) {
                transTemp[index].locale = trans.locale
                transTemp[index].name = trans.name
              }
            })
            return {
              dId: d.id,
              startDate: formatDateTimeInOffset(d.startAt, timezone),
              endDate: formatDateTimeInOffset(d.endAt, timezone),
              translations: transTemp,
              festivalEdition: d.festivalEdition,
            }
          })
          setTimeout(() => {
            setValue('days', temp)
          }, 200)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
  }, [editionId, festivalName, days, allLanguages, timezone])

  const schema = yup.object().shape({
    days: yup.array().of(
      yup
        .object()
        .shape({
          dId: yup.string().optional().nullable(),
          startDate: yup.string().required('Field is required'),
          endDate: yup.string().required('Field is required'),
          translations: yup
            .array()
            .of(
              yup.object().shape({
                locale: yup.string().required('Field is required'),
                name: yup.string().required('Field is required'),
              }),
            )
            .optional(),
          festivalEdition: yup
            .object()
            .shape({
              id: yup.string().required(),
              name: yup.string().required(),
              startDate: yup.number().required(),
              endDate: yup.number().required(),
              active: yup.boolean().required(),
              backstageEventCode: yup.string().optional().nullable(),
            })
            .required(),
        })
        .required(),
    ),
  })

  const {
    handleSubmit,
    register,
    watch,
    control,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<IDatesFormValues>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<any>(schema),
    defaultValues: {},
  })

  const {
    fields: dayFields,
    append: appendDay,
    remove,
  } = useFieldArray({
    control,
    name: `days`,
  })

  const deleteFunc = () => {
    // prettier-ignore
    (async () => {
      try {
        if (editionId && deleteId) {
          const { status } = await deleteFestivalDays(editionId, deleteId)
          const resp = await getFestivalDays(editionId)
          setDays(resp.data)
          if (status === 204) {
            setIsError(false)
            setShowMessageBox(true)
            setMessage('Delete Successful')
          }
        } else if (deleteIndex !== undefined) {
          remove(deleteIndex)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
  }

  const submit = async (values: IDatesFormValues) => {
    try {
      const data: Days[] = map(values.days, (v, index: number) => ({
        id: v.dId || '',
        translations: v.translations,
        festivalEdition:
          day?.[index]?.festivalEdition || day[0].festivalEdition,
        startAt: parseDateTimeInOffset(v.startDate, timezone),
        endAt: parseDateTimeInOffset(v.endDate, timezone),
        backstageEventCode: '',
      }))
      if (editionId) {
        const { status } = await updateFestivalDays(editionId, data)
        if (status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Update Successful')
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  const handleCancel = async () => {
    if (editionId) {
      const temp = map(days, (d) => {
        return {
          dId: d.id,
          startDate: formatDateTimeInOffset(d.startAt, timezone),
          endDate: formatDateTimeInOffset(d.endAt, timezone),
          translations: d.translations,
          festivalEdition: d.festivalEdition,
        }
      })
      setTimeout(() => {
        setValue('days', temp)
      }, 200)
    }
  }

  return (
    <>
      <Card className="p-7.5 bg-gradient-4 border border-monochrome-120">
        <form
          id="settings-festival-dates-form"
          onSubmit={handleSubmit(submit)}
          ref={formRef}
        >
          <h3 className="text-title-xs text-monochrome-1 mb-4">
            Festival Dates
          </h3>
          <div className="mb-4">
            <FormGroup
              label="Show Dates"
              direction="row"
              labelClassName="whitespace-nowrap"
              inputWrapperClassName="w-auto"
              wrapperClassName="w-auto flex-row-reverse justify-end"
              labelProps={{
                htmlFor: 'showDates',
              }}
            >
              <Checkbox
                id="showDates"
                style={{ cursor: 'pointer' }}
                checked={showDates}
                onCheckedChange={(checked) => setShowDates(checked === true)}
                disabled={!hasEditPermission}
              />
            </FormGroup>
          </div>
          {showDates &&
            map(dayFields, (day, index) => {
              const name =
                find(day.translations, (t) => t.locale === 'en')?.name ||
                `Day ${index + 1}`
              return (
                <Card
                  key={day.id}
                  className="p-4 bg-monochrome-3-60 border border-monochrome-120 mt-2"
                >
                  <div className="flex justify-between gap-2">
                    <h6 className="text-title-xs text-monochrome-1">{name}</h6>
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => {
                        setDeleteId(day.dId)
                        setDeleteIndex(index)
                        setDeleteConfirmationModal(true)
                      }}
                      disabled={!hasEditPermission}
                    >
                      Delete
                      <Icon
                        icon="close-circle"
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                        className="shrink-0"
                      />
                    </Button>
                  </div>
                  <div className="flex justify-between gap-2">
                    <FormGroup label="Starting Date">
                      <DateTimePicker
                        className="h-[44px] rounded-2xl"
                        value={
                          parseFormDateTime(watch(`days.${index}.startDate`)) ??
                          null
                        }
                        onChange={(date) => {
                          setValue(
                            `days.${index}.startDate`,
                            date ? formatDateTime(date.toISOString()) : '',
                          )
                        }}
                        placeholder="Enter Starting Date"
                        hasError={!!errors?.days?.[index]?.startDate?.message}
                        disabled={!hasEditPermission}
                        maxDate={
                          parseFormDateTime(watch(`days.${index}.endDate`)) ??
                          undefined
                        }
                      />
                    </FormGroup>
                    <FormGroup label="End Date">
                      <DateTimePicker
                        className="h-[44px] rounded-2xl"
                        value={
                          parseFormDateTime(watch(`days.${index}.endDate`)) ??
                          null
                        }
                        onChange={(date) => {
                          setValue(
                            `days.${index}.endDate`,
                            date ? formatDateTime(date.toISOString()) : '',
                          )
                        }}
                        placeholder="Enter Ending Date"
                        hasError={!!errors?.days?.[index]?.endDate?.message}
                        disabled={!hasEditPermission}
                        minDate={
                          parseFormDateTime(watch(`days.${index}.startDate`)) ??
                          undefined
                        }
                      />
                    </FormGroup>
                  </div>
                  <div className="flex flex-col w-full gap-2 mt-4">
                    {map(allLanguages, (language, i) => {
                      return (
                        <Card
                          className="p-3 space-y-2 w-full"
                          key={language.id}
                        >
                          <FormGroup label="Language" direction="column">
                            <Input
                              value={language.name}
                              readOnly
                              className="bg-monochrome-120"
                            />
                          </FormGroup>
                          <FormGroup label="Translation">
                            <Input
                              isInvalid={
                                !!errors?.days?.[index]?.translations?.[i]?.name
                                  ?.message
                              }
                              {...register(
                                `days.${index}.translations.${i}.name`,
                              )}
                              type="text"
                              disabled={!hasEditPermission}
                            />
                          </FormGroup>
                        </Card>
                      )
                    })}
                  </div>
                </Card>
              )
            })}
        </form>
        <div className="flex justify-center">
          <Button
            onClick={() =>
              appendDay({
                dId: undefined,
                startDate: '',
                endDate: '',
                translations: map(allLanguages, (lang) => {
                  return {
                    locale: lang.locale,
                    name: '',
                  }
                }),
                festivalEdition: day?.[0]?.festivalEdition,
              })
            }
            type="button"
            variant="secondary"
            className="mt-2"
            disabled={!hasEditPermission}
          >
            Add New Day
            <Icon
              icon="plus"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              className="shrink-0"
            />
          </Button>
        </div>

        <div className="flex justify-end gap-2 border-t border-monochrome-120 mt-4 [&>*]:w-[154px] pt-4">
          <Button
            onClick={handleCancel}
            variant="secondary"
            data-dismiss="modal"
            disabled={!hasEditPermission}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            form="settings-festival-dates-form"
            type="submit"
            disabled={isSubmitting || !hasEditPermission}
            loading={isSubmitting}
          >
            Save
          </Button>
        </div>
      </Card>
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={'Day'}
        deleteFunc={deleteFunc}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
